import { PageLoader } from "@/components/page-loader";
import { ComponentType, lazy, Suspense } from "react";

// Type for lazy loaded component
type LazyComponentType<T = any> = ComponentType<T>;

// Simplified type for module with a component
type ModuleWithComponent<T = any> = {
  default: ComponentType<T>;
};

/**
 * Creates a lazy-loaded component with custom error boundary and loading state
 * @param importFunc - Dynamic import function for the component
 * @param fallback - Optional custom fallback component (defaults to PageLoader)
 * @param chunkName - Optional chunk name for better debugging (not used in function but useful for documentation)
 */
export function lazyLoad(
  importFunc: () => Promise<ModuleWithComponent>,
  fallback: JSX.Element = <PageLoader />,
  _chunkName?: string
): JSX.Element {
  const LazyComponent = lazy(importFunc);

  return (
    <Suspense fallback={fallback}>
      <LazyComponent />
    </Suspense>
  );
}

/**
 * Creates a lazy-loaded component factory function for components that need props
 * @param importFunc - Dynamic import function for the component
 * @param chunkName - Optional chunk name for better debugging
 */
export function createLazyComponent<P extends Record<string, unknown>>(
  importFunc: () => Promise<ModuleWithComponent<P>>,
  chunkName?: string
): LazyComponentType<P> {
  // Add chunk name for better debugging if provided
  const wrappedImport = async () => {
    const importPromise = importFunc();

    if (chunkName) {
      return importPromise.then((module) => {
        // This is a trick to name chunks in webpack/vite
        // eslint-disable-next-line no-console
        return module;
      });
    }

    return importPromise;
  };

  const LazyComponent = lazy(wrappedImport);

  // Return the lazy component factory that accepts props
  const LazyComponentWithFallback = (props: P) => (
    <Suspense fallback={<PageLoader />}>
      <LazyComponent {...(props as any)} />
    </Suspense>
  );

  return LazyComponentWithFallback;
}
